'use client';

import { useEffect, useRef, useState } from 'react';

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Centres the burst on whichever element `anchorSelector` matches. Nothing about it is specific to
 * a puzzle grid -- pass any selector and the burst anchors there.
 */
export const useBackgroundBurstPosition = (anchorSelector: string | undefined, isVisible: boolean) => {
  const [position, setPosition] = useState<Position | null>(null);
  const burstRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    // We support passing in undfined here because of the way BackgroundBurst is structured. We
    // need the BackgroundBurst to be able to optionally expand when used in a game context (via
    // GameBackgroundBurst) but we can't hoist the useEffect out of BackgroundBurst without causing
    // all sorts of complications. Hence this hack.
    if (!anchorSelector) return;

    const updatePosition = () => {
      // A selector can match several elements -- a game page renders its board twice, in a mobile
      // and a desktop layout, with the one that isn't showing laid out at zero size. Take the first
      // match that has actual dimensions.
      const candidates = document.querySelectorAll(anchorSelector) as NodeListOf<HTMLElement>;
      let anchor: HTMLElement | null = null;

      for (const candidate of candidates) {
        const rect = candidate.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          anchor = candidate;
          break;
        }
      }

      if (!anchor) {
        return { position: null, burstRef };
      }

      const gridRect = anchor.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate the center of the anchor, accounting for scroll
      const centerX = gridRect.left + gridRect.width / 2 + window.scrollX;
      const centerY = gridRect.top + gridRect.height / 2 + window.scrollY;

      // Get SVG dimensions from the ref
      const svgElement = burstRef.current;
      if (!svgElement) {
        console.log('svgElement not found');
        return { position: null, burstRef };
      }

      // Get the original dimensions from the SVG viewBox or width/height attributes
      const originalWidth = svgElement.viewBox.baseVal.width || svgElement.width.baseVal.value;
      const originalHeight = svgElement.viewBox.baseVal.height || svgElement.height.baseVal.value;

      // Calculate scale factors to make burst fill the screen
      const scaleX = viewportWidth / originalWidth;
      const scaleY = viewportHeight / originalHeight;

      // Use the larger scale to ensure the burst covers the entire screen
      const scale = Math.max(scaleX, scaleY);

      const burstWidth = originalWidth * scale;
      const burstHeight = originalHeight * scale;

      // Center the burst on the anchor
      const left = centerX - burstWidth / 2;
      const top = centerY - burstHeight / 2;

      setPosition({
        x: left,
        y: top,
        width: burstWidth,
        height: burstHeight,
      });
    };

    // Update position on mount and resize
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // Also update when the anchor might change size
    const observer = new ResizeObserver(updatePosition);
    document.querySelectorAll(anchorSelector).forEach((candidate) => {
      observer.observe(candidate);
    });

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
      observer.disconnect();
    };
  }, [anchorSelector, isVisible]);

  return { position, burstRef };
};
